import React, { useState, useEffect } from 'react';
import AttributesTab from './tabs/AttributesTab';
import ActivitiesTab from './tabs/ActivitiesTab';
import RawJsonTab from './tabs/RawJsonTab';
import ScanReportTab from './tabs/ScanReportTab';
import ForensicsTab from './tabs/ForensicsTab';
import '../../styles/v4-theme.css';

/**
 * TargetDetailModal Component
 * Full-screen modal displaying comprehensive target details with tabbed interface
 */
const TargetDetailModal = ({ target, isOpen, onClose, onTimestampClick, inline = false, masterTimeline }) => {
  const [activeTab, setActiveTab] = useState('report');

  // Reset to report tab when modal opens (default landing view)
  useEffect(() => {
    if (isOpen) {
      setActiveTab('report');
      // Lock body scroll
      document.body.style.overflow = 'hidden';
    } else {
      // Unlock body scroll
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen || !target) return null;

  // Check if this is a precision-enhanced target
  const isPrecisionEnhanced = target.precision_enhanced === true && target.vi_verified === true;

  // Define tabs before using them (Scan Report first as default landing view)
  const tabs = [
    { id: 'report', label: 'Scan Report', icon: '▣' },
    { id: 'attributes', label: 'Attributes', icon: '▤' },
    { id: 'activities', label: 'Activities', icon: '▶' },
    // Conditionally add Forensics tab for precision-enhanced targets
    ...(isPrecisionEnhanced ? [{ id: 'forensics', label: 'Forensics', icon: '⚖️' }] : []),
    { id: 'json', label: 'Raw JSON', icon: '{}' }
  ];

  // Inline mode - render without modal overlay
  if (inline) {
    return (
      <div className="v4-detail-content-inline" style={{
        background: 'var(--bg-secondary)',
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-3)',
          background: 'rgba(0, 0, 0, 0.2)',
          borderBottom: '1px solid rgba(148, 163, 184, 0.2)'
        }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="v4-detail-tab"
              style={{
                padding: '6px 14px',
                background: activeTab === tab.id ? 'rgba(124, 58, 237, 0.2)' : 'rgba(59, 130, 246, 0.08)',
                color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
                border: `1px solid ${activeTab === tab.id ? 'rgba(124, 58, 237, 0.6)' : 'rgba(59, 130, 246, 0.3)'}`,
                borderRadius: '6px',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-semibold)',
                cursor: 'pointer',
                transition: 'all var(--transition-base)',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: activeTab === tab.id ? '0 0 0 1px rgba(124, 58, 237, 0.3), 0 2px 8px rgba(124, 58, 237, 0.2)' : '0 0 0 1px rgba(59, 130, 246, 0.15)'
              }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{
          minHeight: '300px',
          background: 'var(--bg-primary)'
        }}>
          {activeTab === 'report' && <ScanReportTab target={target} masterTimeline={masterTimeline} />}
          {activeTab === 'attributes' && <AttributesTab target={target} onTimestampClick={onTimestampClick} />}
          {activeTab === 'activities' && <ActivitiesTab target={target} onTimestampClick={onTimestampClick} />}
          {activeTab === 'forensics' && <ForensicsTab target={target} />}
          {activeTab === 'json' && <RawJsonTab target={target} />}
        </div>
      </div>
    );
  }

  const getTargetIcon = (target) => {
    const targetType = target?.type || target?.target_type;
    const icons = {
      vehicle: '◆',
      person: '●',
      environment: '▲',
      videographer: '■',
      text: '⊞',
      weapon: '▼',
      object: '◇',
      unknown: '○'
    };
    return icons[targetType] || icons.unknown;
  };

  const getTargetTitle = (target) => {
    const targetType = target.type || target.target_type;
    
    if (targetType === 'vehicle') {
      const make = target.attributes?.make || '';
      const model = target.attributes?.model || '';
      const color = target.attributes?.color || '';
      if (make && model) return `${color} ${make} ${model}`.trim();
      if (make) return `${color} ${make}`.trim();
      if (color) return `${color} Vehicle`;
      return 'Vehicle';
    }
    
    if (targetType === 'environment') {
      return target.attributes?.location_type || 'Environment';
    }
    
    if (targetType === 'videographer') {
      return target.attributes?.device_type || 'Videographer';
    }

    return targetType ? targetType.charAt(0).toUpperCase() + targetType.slice(1) : 'Unknown';
  };

  // Modal mode rendering
  return (
    <div
      className="v4-modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 'var(--space-1)'
      }}
      onClick={onClose}
    >
      <div
        className="v4-modal-content"
        style={{
          background: 'var(--bg-secondary)',
          borderRadius: '12px',
          width: '98vw',
          maxWidth: '1400px',
          height: '96vh',
          maxHeight: 'none',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-xl)',
          overflow: 'hidden',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          className="v4-modal-header"
          style={{
            padding: 'var(--space-3)',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg-card)'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
              <div style={{ fontSize: '32px' }}>
                {getTargetIcon(target)}
              </div>
              <div>
                <h2 style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 'var(--font-bold)',
                  color: 'var(--text-primary)',
                  marginBottom: 'var(--space-1)'
                }}>
                  {getTargetTitle(target)}
                </h2>
                <div style={{
                  fontSize: 'var(--text-sm)',
                  color: 'var(--text-secondary)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)'
                }}>
                  <span>ID: {target.id}</span>
                  <span>•</span>
                  <span>Confidence: {Math.round((target.confidence || 0) * 100)}%</span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border)',
                color: 'var(--text-secondary)',
                fontSize: '20px',
                cursor: 'pointer',
                transition: 'all var(--transition-base)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ✕
            </button>
          </div>

          {/* Tab Navigation */}
          <div style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginTop: 'var(--space-2)'
          }}>
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className="v4-detail-tab"
                style={{
                  padding: '6px 14px',
                  background: activeTab === tab.id ? 'rgba(124, 58, 237, 0.2)' : 'rgba(59, 130, 246, 0.08)',
                  color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
                  border: `1px solid ${activeTab === tab.id ? 'rgba(124, 58, 237, 0.6)' : 'rgba(59, 130, 246, 0.3)'}`,
                  borderRadius: '6px',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-semibold)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-base)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: activeTab === tab.id ? '0 0 0 1px rgba(124, 58, 237, 0.3), 0 2px 8px rgba(124, 58, 237, 0.2)' : '0 0 0 1px rgba(59, 130, 246, 0.15)'
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Modal Body - Scrollable */}
        <div
          className="v4-modal-body"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            background: 'var(--bg-primary)',
            WebkitOverflowScrolling: 'touch',
            position: 'relative'
          }}
        >
          {activeTab === 'report' && <ScanReportTab target={target} masterTimeline={masterTimeline} />}
          {activeTab === 'attributes' && <AttributesTab target={target} onTimestampClick={onTimestampClick} />}
          {activeTab === 'activities' && <ActivitiesTab target={target} onTimestampClick={onTimestampClick} />}
          {activeTab === 'forensics' && <ForensicsTab target={target} />}
          {activeTab === 'json' && <RawJsonTab target={target} />}
        </div>
    </div>
  </div>
  );
};

export default TargetDetailModal;

